import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, Toggle, Button } from '../../../../components';
import type { CommsPrefs, CommCategory, CommChannel } from '@/store/slices/userPreferences';
import {
  Categories,
  Category,
  CategoryHead,
  CategoryTitle,
  CategoryHint,
  Channels,
  Channel,
  ChannelLabel,
  Divider,
  SaveError,
  Footer,
} from './CommsSection.styles';

type CategoryCopy = { key: CommCategory; label: string; hint: string; required?: boolean };
type ChannelCopy = { key: CommChannel; label: string };

export type CommsSectionProps = {
  comms: CommsPrefs;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onToggle: (category: CommCategory, channel: CommChannel) => void;
  onSave: () => void;
};

export const CommsSection = ({
  comms,
  dirty,
  saving,
  saveError,
  justSaved,
  onToggle,
  onSave,
}: CommsSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const saveLabel = saving ? t('Saving…') : justSaved ? t('Saved ✓') : t('Save changes');

  // Category copy. `security` is required — its email channel is always on. Labels
  // and hints are translated reactively at render; `key` stays a stable English id.
  const CATEGORIES: CategoryCopy[] = [
    { key: 'product', label: t('Product updates'), hint: t('New features and improvements to your hub.') },
    { key: 'offers', label: t('Promotions & offers'), hint: t('Deals, bonuses and partner offers.') },
    {
      key: 'security',
      label: t('Security & account alerts'),
      hint: t('Sign-ins, password and payout changes. Email is always on.'),
      required: true,
    },
    { key: 'newsletter', label: t('Newsletter & tips'), hint: t('Occasional best-practice tips and product news.') },
  ];

  const CHANNELS: ChannelCopy[] = [
    { key: 'email', label: t('Email') },
    { key: 'sms', label: t('SMS') },
    { key: 'push', label: t('Push') },
  ];

  return (
    <Panel>
      <Categories>
        {CATEGORIES.map((category, index) => (
          <React.Fragment key={category.key}>
            {index > 0 ? <Divider /> : null}
            <Category>
              <CategoryHead>
                <CategoryTitle text={category.label} tag="span" />
                <CategoryHint text={category.hint} tag="span" />
              </CategoryHead>
              <Channels>
                {CHANNELS.map((channel) => {
                  const required = category.required === true && channel.key === 'email';
                  return (
                    <Channel key={channel.key}>
                      <ChannelLabel text={channel.label} tag="span" />
                      <Toggle
                        on={comms[category.key][channel.key]}
                        label={t('{{channel}} · {{category}}')}
                        labelValues={{ channel: channel.label, category: category.label }}
                        disabled={required}
                        onToggle={() => onToggle(category.key, channel.key)}
                      />
                    </Channel>
                  );
                })}
              </Channels>
            </Category>
          </React.Fragment>
        ))}
      </Categories>
      {saveError ? <SaveError text={saveError} tag="span" /> : null}
      <Footer>
        <Button label={saveLabel} fullWidth disabled={!dirty || saving} onClick={onSave} />
      </Footer>
    </Panel>
  );
};

export default CommsSection;
